import { useCallback, useRef, useState } from "react";

/**
 * Function signature for fetch operations that support cancellation.
 * @template T - The expected return type of the fetch operation
 * @param signal - AbortSignal used to cancel the request
 * @param args - Additional arguments to pass to the fetch function
 */
type FetchFn<T> = (signal: AbortSignal, ...args: any[]) => Promise<T>;

/**
 * Result object returned by the runFetch function.
 * @template T - The type of the data returned on success
 */
type FetchResult<T> = {
  /** Whether the fetch operation completed successfully */
  success: boolean;
  /** The fetched data if successful, null otherwise */
  data: T | null;
  /** Error object if the fetch failed, null otherwise */
  error: any;
};

/**
 * A React hook for making abortable fetch requests.
 *
 * This hook provides automatic request cancellation when a new request is made,
 * preventing race conditions and stale data. It also exposes loading, error,
 * and data states for convenient UI integration.
 *
 * @template T - The expected return type of the fetch operation
 * @param fetchFn - A function that accepts an AbortSignal as its first parameter and returns a Promise
 * @returns An object containing:
 *   - `runFetch`: Function to trigger the fetch operation
 *   - `abort`: Function to manually cancel the current request
 *   - `data`: The fetched data (null if not yet fetched or on error)
 *   - `error`: The error object if the fetch failed
 *   - `loading`: Boolean indicating if a fetch is in progress
 *
 * @example
 * ```tsx
 * const { runFetch, data, loading, error } = useAbortableFetch(
 *   (signal) => fetchUserData(signal, userId)
 * );
 *
 * const handleClick = async () => {
 *   const result = await runFetch();
 *   if (result.success) {
 *     console.log('Data fetched:', result.data);
 *   }
 * };
 * ```
 */
export function useAbortableFetch<T = any>(fetchFn: FetchFn<T>) {
  const abortControllerRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const runFetch = useCallback(
    async (...args: any[]): Promise<FetchResult<T>> => {
      abortControllerRef.current?.abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        const result = await fetchFn(controller.signal, ...args);
        setData(result);

        return {
          success: true,
          data: result,
          error: null,
        };
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError(err);
        }

        return {
          success: false,
          data: null,
          error: err,
        };
      } finally {
        setLoading(false);
      }
    },
    [fetchFn],
  );
  return { runFetch, abort, data, error, loading };
}
